import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, User, ShoppingBag, Menu, X, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { LOGO_PINK, NAV_LINKS } from "../../lib/data";
import { useCart } from "../../context/CartContext";

export const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const { count, setCartOpen } = useCart();
    const { pathname } = useLocation();
    const isHome = pathname === "/";
    const solid = scrolled || !isHome;

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => setMenuOpen(false), [pathname]);

    const soon = (label) => toast(`${label} arrives with the full shop in Phase 2.`);

    return (
        <>
            <header
                data-testid="site-header"
                className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
                    solid
                        ? "bg-white/85 backdrop-blur-xl border-b border-zinc-200"
                        : "bg-transparent border-b border-transparent"
                }`}
            >
                <div className="mx-auto max-w-[1440px] px-5 md:px-10 h-20 md:h-24 flex items-center justify-between gap-4">
                    <Link to="/" data-testid="header-logo-link" className="shrink-0">
                        <img
                            src={LOGO_PINK}
                            alt="Showtime Carnival Co."
                            className="h-12 md:h-16 w-auto"
                        />
                    </Link>

                    <nav data-testid="desktop-nav" className="hidden lg:flex items-center gap-7">
                        {NAV_LINKS.map((link) => (
                            <NavLink
                                key={link.to}
                                to={link.to}
                                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={({ isActive }) =>
                                    `font-manrope text-[13px] font-bold tracking-[0.18em] uppercase transition-colors duration-300 ${
                                        solid
                                            ? isActive ? "text-pink-dark" : "text-ink hover:text-pink-dark"
                                            : isActive ? "text-pink" : "text-white hover:text-pink"
                                    }`
                                }
                            >
                                {link.label}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="flex items-center gap-1 md:gap-2">
                        <button
                            onClick={() => soon("Search")}
                            data-testid="header-search-button"
                            aria-label="Search"
                            className={`hidden md:flex h-10 w-10 items-center justify-center rounded-full transition-colors duration-300 ${solid ? "text-ink hover:bg-paper" : "text-white hover:bg-white/10"}`}
                        >
                            <Search size={18} />
                        </button>
                        <button
                            onClick={() => soon("Accounts")}
                            data-testid="header-account-button"
                            aria-label="Account"
                            className={`hidden md:flex h-10 w-10 items-center justify-center rounded-full transition-colors duration-300 ${solid ? "text-ink hover:bg-paper" : "text-white hover:bg-white/10"}`}
                        >
                            <User size={18} />
                        </button>
                        <button
                            onClick={() => setCartOpen(true)}
                            data-testid="header-cart-button"
                            aria-label="Cart"
                            className={`relative h-10 w-10 flex items-center justify-center rounded-full transition-colors duration-300 ${solid ? "text-ink hover:bg-paper" : "text-white hover:bg-white/10"}`}
                        >
                            <ShoppingBag size={18} />
                            {count > 0 && (
                                <span data-testid="cart-count-badge" className="absolute -top-0.5 -right-0.5 h-5 min-w-5 px-1 rounded-full bg-pink text-ink text-[10px] font-extrabold flex items-center justify-center">
                                    {count}
                                </span>
                            )}
                        </button>
                        <Link
                            to="/book"
                            data-testid="header-book-cta"
                            className="hidden md:inline-flex ml-2 items-center gap-1.5 rounded-full bg-pink px-5 py-2.5 font-oswald text-sm uppercase tracking-widest text-ink hover:bg-ink hover:text-white transition-colors duration-300 active:scale-95"
                        >
                            Book The Show <ArrowUpRight size={15} />
                        </Link>
                        <button
                            onClick={() => setMenuOpen(true)}
                            data-testid="mobile-menu-button"
                            aria-label="Open menu"
                            className={`lg:hidden h-10 w-10 flex items-center justify-center rounded-full ${solid ? "text-ink" : "text-white"}`}
                        >
                            <Menu size={22} />
                        </button>
                    </div>
                </div>
            </header>

            <AnimatePresence>
                {menuOpen && (
                    <motion.div
                        data-testid="mobile-menu"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed inset-0 z-[60] bg-ink tent-stripes flex flex-col"
                    >
                        <div className="flex items-center justify-between px-5 h-16">
                            <img src={LOGO_PINK} alt="Showtime Carnival Co." className="h-14 w-auto" />
                            <button
                                onClick={() => setMenuOpen(false)}
                                data-testid="mobile-menu-close-button"
                                aria-label="Close menu"
                                className="h-10 w-10 flex items-center justify-center rounded-full text-white hover:bg-white/10"
                            >
                                <X size={22} />
                            </button>
                        </div>
                        <nav className="flex-1 flex flex-col justify-center px-8 gap-1">
                            {NAV_LINKS.map((link, i) => (
                                <motion.div
                                    key={link.to}
                                    initial={{ opacity: 0, x: -24 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                                >
                                    <NavLink
                                        to={link.to}
                                        data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                                        className="block font-oswald uppercase text-4xl py-2 text-white hover:text-pink transition-colors duration-300"
                                    >
                                        {link.label}
                                    </NavLink>
                                </motion.div>
                            ))}
                        </nav>
                        <div className="px-8 pb-10">
                            <Link
                                to="/book"
                                data-testid="mobile-menu-book-cta"
                                className="flex items-center justify-center gap-2 w-full rounded-full bg-pink py-4 font-oswald uppercase tracking-widest text-ink active:scale-95 transition-transform"
                            >
                                Book The Show <ArrowUpRight size={16} />
                            </Link>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};
